/**
 * Your changes to the rules (V1-E00-T02): the tenant's own overrides, within what its profile allows.
 * Calling hours have their own fields; any other section is edited as JSON. The API refuses a change
 * that loosens a rule, whole and with the rule's name, and that refusal is shown as it says it.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId, useState, type FormEvent } from "react";
import { ApiError, data, type Schemas } from "../api/client";
import { useBff } from "../app/session";
import { useT } from "../i18n";
import { Button } from "../ui/Button";

type Effective = Schemas["EffectiveProfile"];
type Overrides = Record<string, unknown>;
interface Window { start?: string; end?: string }

const field = "rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-sm";

/** The overrides with the default calling window's hours set, the rest of the section kept. */
export function withHours(overrides: Overrides, start: string, end: string): Overrides {
  const section = (overrides.calling_window ?? {}) as Record<string, unknown>;
  const window = (section.default ?? {}) as Window;
  return { ...overrides, calling_window: { ...section, default: { ...window, start, end } } };
}

function refusal(error: unknown): string | null {
  if (!error) return null;
  if (error instanceof ApiError) return error.problem.errors?.[0]?.message ?? error.message;
  return error instanceof Error ? error.message : String(error);
}

export function YourChanges({ effective }: { effective: Effective }) {
  const t = useT();
  const id = useId();
  const { api } = useBff();
  const queries = useQueryClient();
  const spec = effective.effective as unknown as Record<string, Record<string, unknown>>;
  const window = (spec.calling_window?.default ?? null) as Window | null;
  const hoursMay = spec.calling_window?.tenant_may as string | undefined;
  const [hours, setHours] = useState({ start: window?.start ?? "", end: window?.end ?? "" });
  const [text, setText] = useState(JSON.stringify(effective.overrides, null, 2));
  const [textProblem, setTextProblem] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (overrides: Overrides) => data(api.PUT("/v1/compliance/overrides", { body: { overrides } })),
    onSuccess: async (saved) => {
      setText(JSON.stringify(saved.overrides, null, 2));
      await queries.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith("compliance-effective") });
    },
  });
  const problem = refusal(save.error);

  const saveHours = (e: FormEvent) => {
    e.preventDefault();
    save.mutate(withHours(effective.overrides, hours.start, hours.end));
  };
  const saveText = (e: FormEvent) => {
    e.preventDefault();
    let parsed: unknown;
    try {
      parsed = JSON.parse(text || "{}");
    } catch {
      setTextProblem(t("compliance.changes.notJson"));
      return;
    }
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      setTextProblem(t("compliance.changes.notObject"));
      return;
    }
    setTextProblem(null);
    save.mutate(parsed as Overrides);
  };

  return (
    <section aria-labelledby={`${id}-title`} className="space-y-4 rounded-lg border border-[var(--color-border)] p-4">
      <header className="space-y-1">
        <h3 id={`${id}-title`} className="font-semibold">{t("compliance.changes.title")}</h3>
        <p className="text-sm text-[var(--color-muted)]">{t("compliance.changes.intro")}</p>
      </header>

      {hoursMay !== "none" && (
        <form onSubmit={saveHours} className="flex flex-wrap items-end gap-3" aria-label={t("compliance.changes.hours")}>
          <div className="space-y-1">
            <label htmlFor={`${id}-start`} className="block text-sm font-medium">{t("compliance.changes.start")}</label>
            <input id={`${id}-start`} type="time" required className={field} value={hours.start}
              onChange={(e) => setHours({ ...hours, start: e.target.value })} />
          </div>
          <div className="space-y-1">
            <label htmlFor={`${id}-end`} className="block text-sm font-medium">{t("compliance.changes.end")}</label>
            <input id={`${id}-end`} type="time" required className={field} value={hours.end}
              onChange={(e) => setHours({ ...hours, end: e.target.value })} />
          </div>
          <Button type="submit" disabled={save.isPending}>{t("compliance.changes.saveHours")}</Button>
          <p className="basis-full text-xs text-[var(--color-muted)]">{t("compliance.changes.hoursHint")}</p>
        </form>
      )}

      <details>
        <summary className="cursor-pointer text-sm text-[var(--color-primary)]">{t("compliance.changes.advanced")}</summary>
        <form onSubmit={saveText} className="mt-2 space-y-2">
          <label htmlFor={`${id}-json`} className="block text-sm font-medium">{t("compliance.changes.json")}</label>
          <textarea id={`${id}-json`} rows={8} spellCheck={false} className={`${field} w-full font-mono text-xs`}
            value={text} onChange={(e) => setText(e.target.value)}
            aria-invalid={textProblem ? true : undefined} aria-describedby={`${id}-json-hint`} />
          <p id={`${id}-json-hint`} className="text-xs text-[var(--color-muted)]">{textProblem ?? t("compliance.changes.jsonHint")}</p>
          <div className="flex gap-2">
            <Button type="submit" disabled={save.isPending}>{t("compliance.changes.save")}</Button>
            <Button type="button" variant="secondary" disabled={save.isPending} onClick={() => save.mutate({})}>
              {t("compliance.changes.clear")}
            </Button>
          </div>
        </form>
      </details>

      {problem && (
        <p role="alert" className="rounded-md border border-[var(--color-danger)] p-3 text-sm">
          {t("compliance.changes.refused", { why: problem })}
        </p>
      )}
      {save.isSuccess && !problem && <p role="status" className="text-sm">{t("compliance.changes.saved")}</p>}
    </section>
  );
}
